import { Upload } from 'lucide-react';
import { formatBytes } from '../../lib/folderUpload';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import { clean } from '../../shell/copy';
import { plural } from './archive';

// ── Folder upload pre-flight ────────────────────────────────────────────────────
// No folder upload starts without this: the depth and folder caps reject at CREATION time, so without the dry run a
// large upload can die partway and leave a half-built tree. A merge into existing folders is said in plain words.
// Moved here from pages/Storage.jsx (HD-9) so the Portal page uses the same dialog; note: the line after the destination
// (the Console wording by default; the Portal page passes its own, without staff terms).

const STAFF_NOTE = 'Files uploaded here go to cold archive storage; they do not appear in Raw footage until promoted. The folder structure is recreated here.';

export default function PreflightModal({ preflight, destination, onCancel, onConfirm, note = STAFF_NOTE }) {
  const { walking, count, summary, dry, error } = preflight;
  const blocked = !!error;
  const merging = dry ? dry.existingCount > 0 : false;
  const title = walking ? 'Reading the folder' : blocked ? 'This folder cannot be uploaded' : 'Confirm folder upload';
  return (
    <Modal open onClose={onCancel} title={title}
      footer={(
        <>
          <Button variant="quiet" onClick={onCancel}>{blocked ? 'Close' : 'Cancel'}</Button>
          {!walking && !blocked && <Button variant="primary" icon={Upload} onClick={onConfirm}>Upload {plural(summary.fileCount, 'file')}</Button>}
        </>
      )}>
      <div className="ui-stack">
        <p className="ui-p ui-dense ui-text-2">Destination: {destination}. {note}</p>
        {walking ? (
          <p className="ui-p" role="status">{plural(count, 'file')} found so far.</p>
        ) : blocked ? (
          <p className="ui-p" role="alert"><Badge tone="danger">Blocked</Badge> {error}</p>
        ) : (
          <>
            <dl className="ui-kv">
              <dt>Files</dt><dd>{Number(summary.fileCount).toLocaleString()}</dd>
              <dt>Folders</dt><dd>{Number(summary.folderCount).toLocaleString()}</dd>
              <dt>Total size</dt><dd className="ui-tc">{formatBytes(summary.totalBytes)}</dd>
              <dt>New folders</dt><dd>{Number(dry.createdCount).toLocaleString()}</dd>
              <dt>Existing folders</dt><dd>{Number(dry.existingCount).toLocaleString()} added to</dd>
            </dl>
            {merging && (
              <p className="ui-p ui-dense"><Badge tone="warn">Merges</Badge> Some of these files will be added to folders that already exist. If you meant to create a fresh folder, cancel and check the destination first.</p>
            )}
            {dry.sanitized?.length > 0 && (
              <div className="ui-stack ui-stack--tight">
                <span className="ui-dense ui-text-2">{plural(dry.sanitized.length, 'folder name')} will be cleaned up:</span>
                <ul className="ui-plainlist ui-scroll ui-dense ui-text-2">
                  {dry.sanitized.slice(0, 25).map((s, i) => <li key={i}>{clean(s.from)} becomes {clean(s.to)}</li>)}
                  {dry.sanitized.length > 25 && <li>and {dry.sanitized.length - 25} more</li>}
                </ul>
              </div>
            )}
            {summary.skippedJunk > 0 && (
              <p className="ui-p ui-meta">{plural(summary.skippedJunk, 'system file')} skipped (.DS_Store, Thumbs.db and similar).</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
